<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Container, InputV2, Text, TokenV2 } from '@Shelly/shelly-design-system';
	import { requestEditDevice } from 'src/components/Lists/list-helpers';
	import {
		celsius_to_fahrenheit,
		fahrenheit_to_celsius,
		isBluTRV,
	} from 'src/helpers/interface_helpers';
	import { capFirstChar, getErrorOrInfoMessageForMinAndMax } from 'src/helpers/utilities';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import { callBluTRV, getTrvComponentByDevice } from 'src/helpers/virtual_helpers/blutrv_helpers';
	import type { Device } from 'src/types/Device';

	export let device: Device;

	const min = -12.5;
	const max = 12.5;
	const savedOffset = getTrvComponentByDevice(device)?.config?.['temperature:0']?.offset_C || 0;
	const savedUnit: 'C' | 'F' =
		device?.ht_ui?.temperature_unit === 'F' ? 'F' : 'C';

	let selectedUnit: 'C' | 'F' = savedUnit;
	let offsetValue: number = savedOffset;
	let waitForResponse = false;
	let lastSavedUnit: 'C' | 'F' = savedUnit;
	let lastSavedOffset: number = savedOffset;

	$: numericOffset = Number(offsetValue);
	$: offsetInputData = getErrorOrInfoMessageForMinAndMax(numericOffset, min, max);
	$: unitsDirty = selectedUnit !== lastSavedUnit;
	$: offsetDirty = numericOffset !== lastSavedOffset;
	$: isDirty = unitsDirty || offsetDirty;
	$: saveDisabled = waitForResponse || !isDirty || !!offsetInputData.error;

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		setStickyBottomAction({
			onClick: save,
			loading: waitForResponse,
			disabled: saveDisabled,
			icon: 'fas fa-save',
		});
	}

	const saveUnits = async () => {
		let editDevice = {
			...device,
			ht_ui: { temperature_unit: selectedUnit },
		};

		if (isBluTRV(device?.type) && device?.temp_fav_positions) {
			editDevice = {
				...editDevice,
				temp_fav_positions: device.temp_fav_positions.map((pos) => ({
					...pos,
					value:
						selectedUnit === 'C'
							? +fahrenheit_to_celsius(pos.value).toFixed(1)
							: celsius_to_fahrenheit(pos.value),
				})),
			};
		}

		const res = await requestEditDevice(editDevice);
		if (res?.isok) {
			lastSavedUnit = selectedUnit;
		}
	};

	const saveOffset = async () => {
		const res = await callBluTRV(device, 'Temperature.SetConfig', {
			id: 0,
			config: { offset_C: numericOffset },
		});
		if (res?.isok) {
			lastSavedOffset = numericOffset;
		}
	};

	const save = async () => {
		if (saveDisabled) return;
		waitForResponse = true;
		if (unitsDirty) await saveUnits();
		if (offsetDirty && !offsetInputData.error) await saveOffset();
		waitForResponse = false;
	};
</script>

<div class="blu-trv-temperature-settings">
	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<Text styleType="h2" class="mb-s">
			{capFirstChar(
				$format('sub_settings_title_temperature_unit', {
					default: 'Temperature units',
				}),
			)}
		</Text>
		<Text styleType="body-small" class="mb-s">
			{$format('select_temperature_units', {
				default: 'Choose temperature units:',
			})}
		</Text>
		<div class="d-flex">
			<TokenV2
				id="token-blu-trv-unit-c"
				selected={selectedUnit === 'C'}
				size="m"
				title="°C"
				class="mr-s"
				on:click={() => {
					selectedUnit = 'C';
				}}
			/>
			<TokenV2
				id="token-blu-trv-unit-f"
				selected={selectedUnit === 'F'}
				size="m"
				title="°F"
				on:click={() => {
					selectedUnit = 'F';
				}}
			/>
		</div>
	</Container>

	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<Text styleType="h2" class="mb-s">
			{capFirstChar(
				$format('sub_settings_title_temperature_offset', {
					default: 'temperature offset',
				}),
			)}
		</Text>
		<Text styleType="body-small" class="mb-s">
			{$format('text_trv_offset_description', {
				default:
					'Offset in °{units} to be applied to the measured temperature. The value must be between {min}°{units} and {max}°{units}.',
				values: { units: 'C', min, max },
			})}
		</Text>
		<InputV2
			aria-label={$format('input_temperature_offset', {
				default: 'Enter the temperature offset. Value must be between {min} — {max}!',
				values: { min, max },
			})}
			{...getNumberInputConfig(true)}
			icon={null}
			size="m"
			width="full"
			bind:value={offsetValue}
			info={offsetInputData.info}
			error={offsetInputData.error}
			id="input-blu-trv-temp-offset"
		/>
	</Container>
</div>

<style>
	.blu-trv-temperature-settings {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}
</style>
